import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  Check,
  Copy,
  Edit3,
  ExternalLink,
  Leaf,
  MoreHorizontal,
  PackagePlus,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  Weight,
} from 'lucide-react';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { numberValue } from '../lib/pricing';
import ConfirmDialog from './ConfirmDialog';
import MaterialForm from './MaterialForm';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return Number(value) || 0;
}

function readableError(error) {
  const code = error?.code || '';
  if (code.includes('permission-denied')) return 'Firestore blocked your material inventory. Confirm the published rules allow your user subcollections.';
  if (code.includes('unavailable')) return 'MM could not reach Firebase. Check your connection and try again.';
  return error?.message || 'The material inventory could not be updated.';
}

export default function Materials({ user, onRequestAuth }) {
  const [materials, setMaterials] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!user || !db) {
      setMaterials([]);
      setProfiles([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const stopMaterials = onSnapshot(collection(db, 'users', user.uid, 'materials'), (snapshot) => {
      setMaterials(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => timestampValue(b.updatedAt) - timestampValue(a.updatedAt)));
      setLoading(false);
    }, (snapshotError) => { setError(readableError(snapshotError)); setLoading(false); });
    const stopProfiles = onSnapshot(collection(db, 'users', user.uid, 'costProfiles'), (snapshot) => setProfiles(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), (snapshotError) => setError(readableError(snapshotError)));
    return () => { stopMaterials(); stopProfiles(); };
  }, [user]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const visibleMaterials = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return materials;
    return materials.filter((material) => [material.name, material.brand, material.materialType, material.color].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [materials, query]);

  const inventory = useMemo(() => materials.reduce((summary, material) => {
    const remaining = Math.max(0, numberValue(material.remainingGrams));
    const fullWeight = Math.max(0, numberValue(material.spoolWeightGrams));
    summary.remaining += remaining;
    summary.value += fullWeight > 0 ? remaining * (numberValue(material.spoolPrice) / fullWeight) : 0;
    if (remaining <= numberValue(material.lowStockThresholdGrams)) summary.low += 1;
    return summary;
  }, { remaining: 0, value: 0, low: 0 }), [materials]);
  const typeCount = new Set(materials.map((material) => material.materialType || 'Other')).size;

  const saveMaterial = async (values) => {
    if (!user || !db) throw new Error('Sign in before saving material inventory.');
    setWorking(true);
    try {
      const reference = editor?.id ? doc(db, 'users', user.uid, 'materials', editor.id) : doc(collection(db, 'users', user.uid, 'materials'));
      await setDoc(reference, { ...values, ownerId: user.uid, ...(editor?.id ? {} : { createdAt: serverTimestamp() }), updatedAt: serverTimestamp() }, { merge: true });
      setEditor(null);
      setToast(editor?.id ? 'Material inventory updated.' : 'Material added to inventory.');
    } catch (saveError) {
      throw new Error(readableError(saveError));
    } finally {
      setWorking(false);
    }
  };

  const duplicateMaterial = async (material) => {
    if (!user || !db) return;
    setOpenMenu(null);
    setWorking(true);
    try {
      const copy = { ...material };
      delete copy.id;
      delete copy.createdAt;
      delete copy.updatedAt;
      await setDoc(doc(collection(db, 'users', user.uid, 'materials')), { ...copy, name: `${material.name || 'Material'} Copy`, ownerId: user.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      setToast('Material duplicated.');
    } catch (duplicateError) {
      setError(readableError(duplicateError));
    } finally {
      setWorking(false);
    }
  };

  const deleteMaterial = async () => {
    if (!user || !db || !deleteTarget) return;
    setWorking(true);
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'materials', deleteTarget.id));
      setDeleteTarget(null);
      setToast('Material removed from inventory.');
    } catch (deleteError) {
      setError(readableError(deleteError));
    } finally {
      setWorking(false);
    }
  };

  if (!user) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><Leaf size={30} /></span><p className="eyebrow">Private material inventory</p><h1>Sign in to track your filament.</h1><p>See remaining weight, inventory value, and low-stock warnings from any device.</p>
        <div className="signed-out-state__actions"><button className="button button--primary button--large" type="button" onClick={() => onRequestAuth('signup')}>Create account</button><button className="button button--ghost button--large" type="button" onClick={() => onRequestAuth('signin')}>Sign in</button></div>
      </section>
    );
  }

  return (
    <section className="profiles-page feature-page">
      <header className="profiles-page__hero">
        <div><p className="eyebrow">Material inventory · Live</p><h1>Know what is left before the next job.</h1><p>Track filament by brand, color, weight, cost, and linked pricing profile—with low-stock warnings before a quote becomes a problem.</p></div>
        <button className="button button--primary button--large" type="button" onClick={() => setEditor({})}><Plus size={19} /> Add material</button>
      </header>

      {error && <div className="config-banner config-banner--error" role="alert"><ShieldCheck size={18} /><span>{error}</span><button type="button" onClick={() => setError('')}>Dismiss</button></div>}

      <section className="profiles-summary">
        <article><span><Boxes size={19} /></span><div><small>Tracked spools</small><strong>{materials.length}</strong></div></article>
        <article><span><Weight size={19} /></span><div><small>Filament remaining</small><strong>{(inventory.remaining / 1000).toFixed(2)} kg</strong></div></article>
        <article><span><PackagePlus size={19} /></span><div><small>Inventory value</small><strong>{currency.format(inventory.value)}</strong></div></article>
        <article className={inventory.low ? 'summary-warning' : ''}><span><AlertTriangle size={19} /></span><div><small>Low-stock spools</small><strong>{inventory.low}</strong></div></article>
      </section>

      <section className="feature-toolbar brushed-panel"><label className="estimates-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search brand, material, color, or spool" /></label><span>{visibleMaterials.length} of {materials.length} spool{materials.length === 1 ? '' : 's'} · {typeCount} types</span></section>

      <section className="feature-library brushed-panel">
        {loading ? <div className="profiles-loading" aria-live="polite"><span /><span /><span /><p>Loading material inventory…</p></div> : materials.length === 0 ? (
          <div className="profiles-empty"><span><Leaf size={28} /></span><h3>Your filament shelf will live here.</h3><p>Add the spool you use most, enter its remaining weight, and MM will watch the stock threshold.</p><button className="button button--primary" type="button" onClick={() => setEditor({})}><Plus size={18} /> Add first material</button></div>
        ) : visibleMaterials.length === 0 ? (
          <div className="profiles-empty"><span><Search size={28} /></span><h3>No materials match that search.</h3><p>Try a brand, material type, color, or inventory name.</p></div>
        ) : (
          <div className="feature-card-grid">
            {visibleMaterials.map((material) => {
              const remaining = Math.max(0, numberValue(material.remainingGrams));
              const fullWeight = Math.max(1, numberValue(material.spoolWeightGrams, 1));
              const percent = Math.min(100, (remaining / fullWeight) * 100);
              const low = remaining <= numberValue(material.lowStockThresholdGrams);
              const linkedProfile = profiles.find((profile) => profile.id === material.linkedProfileId);
              return (
                <article className={`feature-card material-card ${low ? 'material-card--low' : ''}`} key={material.id}>
                  <div className="feature-card__head"><span className="feature-card__icon"><Leaf size={20} /></span><div><h2>{material.name || 'Unnamed material'}</h2><p>{material.brand || 'Unbranded'} · {material.materialType || 'Other'}{material.color ? ` · ${material.color}` : ''}</p></div><div className="profile-card__menu-wrap"><button className="icon-button profile-card__menu" type="button" onClick={() => setOpenMenu(openMenu === material.id ? null : material.id)} aria-label={`Actions for ${material.name || 'material'}`} aria-expanded={openMenu === material.id}><MoreHorizontal size={18} /></button>{openMenu === material.id && <div className="profile-menu"><button type="button" onClick={() => { setEditor(material); setOpenMenu(null); }}><Edit3 size={16} /> Edit material</button><button type="button" onClick={() => duplicateMaterial(material)}><Copy size={16} /> Duplicate</button><button className="profile-menu__danger" type="button" onClick={() => { setDeleteTarget(material); setOpenMenu(null); }}><Trash2 size={16} /> Delete</button></div>}</div></div>
                  <div className="stock-meter"><span><small>{low ? 'Low stock' : 'Available'}</small><strong>{remaining.toFixed(0)} g</strong></span><div><i style={{ width: `${percent}%` }} /></div><small>{percent.toFixed(0)}% of {fullWeight.toFixed(0)} g</small></div>
                  <div className="feature-card__metrics"><span><small>Spool price</small><strong>{currency.format(numberValue(material.spoolPrice))}</strong></span><span><small>Low alert</small><strong>{numberValue(material.lowStockThresholdGrams)} g</strong></span><span><small>Cost profile</small><strong>{linkedProfile?.name || 'Not linked'}</strong></span></div>
                  <footer className="feature-card__actions"><button className="button button--ghost" type="button" onClick={() => setEditor(material)}><Edit3 size={15} /> Update stock</button>{material.productUrl ? <a className="button button--primary" href={material.productUrl} target="_blank" rel="noreferrer">Product page <ExternalLink size={15} /></a> : <span className="material-status"><Check size={14} /> Tracking</span>}</footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {editor && <MaterialForm material={editor.id ? editor : null} profiles={profiles} saving={working} onClose={() => !working && setEditor(null)} onSave={saveMaterial} />}
      {deleteTarget && <ConfirmDialog title={`Remove ${deleteTarget.name || 'this material'}?`} message="This removes the inventory record. Cost profiles, catalog products, and saved estimates are not deleted." working={working} onCancel={() => !working && setDeleteTarget(null)} onConfirm={deleteMaterial} />}
      {toast && <div className="toast" role="status"><Check size={17} /> {toast}</div>}
    </section>
  );
}
